import { useState, useEffect } from 'react';
import { useAdminTheme } from '@/context/ThemeContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ImageFieldWithPicker } from '@/components/admin/AssetPickerDialog';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Save, 
  X, 
  Users,
  ArrowUp,
  ArrowDown,
  Linkedin,
  RefreshCw
} from 'lucide-react';

const API_URL = window.location.origin;

export default function TeamManager() {
  const { isDark } = useAdminTheme();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    department: '',
    image_url: '',
    linkedin_url: '',
    bio: '',
    order: 0,
    is_active: true
  });

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/team/`);
      const data = await res.json();
      setTeamMembers(data.team_members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Seed default team
  const seedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/seed-defaults`, { method: 'POST' });
      const data = await res.json();
      alert(`Seeded: ${data.seeded}, Updated: ${data.updated}`);
      fetchTeamMembers();
    } catch (error) {
      console.error('Error seeding defaults:', error);
    }
  };

  // Add new member
  const handleAdd = async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          order: teamMembers.length + 1
        })
      });
      if (res.ok) {
        setShowAddForm(false);
        resetForm();
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error adding team member:', error);
    }
  };

  // Update member
  const handleUpdate = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/team/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setEditingId(null);
        resetForm();
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error updating team member:', error);
    }
  };

  // Delete member
  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/team/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
    }
  };

  // Reorder member
  const handleReorder = async (id, direction) => {
    const member = teamMembers.find(m => m.id === id);
    const newOrder = direction === 'up' ? member.order - 1 : member.order + 1;
    
    try {
      await fetch(`${API_URL}/api/team/${id}/reorder?new_order=${newOrder}`, { method: 'PUT' });
      fetchTeamMembers();
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  // Toggle active status
  const handleToggleActive = async (member) => {
    try {
      await fetch(`${API_URL}/api/team/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !member.is_active })
      });
      fetchTeamMembers();
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      department: '',
      image_url: '',
      linkedin_url: '',
      bio: '',
      order: 0,
      is_active: true
    });
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setFormData({
      name: member.name,
      role: member.role,
      department: member.department,
      image_url: member.image_url,
      linkedin_url: member.linkedin_url || '',
      bio: member.bio || '',
      order: member.order,
      is_active: member.is_active
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetForm();
  };

  return (
    <AdminLayout>
    <div className={`min-h-screen ${isDark ? 'bg-[#0f1a20]' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Users className="inline-block w-6 h-6 mr-2" />
              Leadership Team Manager
            </h1>
            <p className={`mt-1 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              Manage the senior leadership team displayed on the About page
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={seedDefaults}
              variant="outline"
              className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Defaults
            </Button>
            <Button 
              onClick={() => setShowAddForm(true)}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>

        {/* Add Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-2xl rounded-2xl p-6 ${isDark ? 'bg-[#1a2830]' : 'bg-white'} shadow-xl`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Add New Team Member
                </h2>
                <Button variant="ghost" size="icon" onClick={() => { setShowAddForm(false); resetForm(); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Full Name"
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Role *</label>
                  <Input
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    placeholder="e.g., Director - Operations"
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Department *</label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    placeholder="e.g., Leadership, Operations"
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>LinkedIn URL</label>
                  <Input
                    value={formData.linkedin_url}
                    onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                    placeholder="https://linkedin.com/in/..."
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                </div>
                <div className="col-span-2">
                  <ImageFieldWithPicker
                    label="Team Member Photo *"
                    value={formData.image_url}
                    onChange={(url) => setFormData({...formData, image_url: url})}
                    onClear={() => setFormData({...formData, image_url: ''})}
                    previewShape="rounded-full"
                    uploadCategory="team"
                    uploadAssetKeyPrefix="team_member"
                    placeholder="Select team member photo"
                  />
                </div>
                <div className="col-span-2">
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Bio</label>
                  <Input
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="Short bio or tagline"
                    className={isDark ? 'bg-white/5 border-white/10 text-white' : ''}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => { setShowAddForm(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleAdd}
                  disabled={!formData.name || !formData.role || !formData.department || !formData.image_url}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Team Members List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : teamMembers.length === 0 ? (
          <div className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-[#1a2830] border-white/10' : 'bg-white border-gray-200'}`}>
            <Users className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>No team members yet</h3>
            <p className={`mt-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>Click "Seed Defaults" to load the default team or add members manually.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {teamMembers.map((member, index) => (
              <div 
                key={member.id}
                className={`rounded-xl border p-4 transition-all ${
                  isDark 
                    ? 'bg-[#1a2830] border-white/10 hover:border-white/20' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                } ${!member.is_active ? 'opacity-50' : ''}`}
              >
                {editingId === member.id ? (
                  // Edit Mode - White background container for visibility
                  <div className="space-y-4 bg-white p-4 rounded-lg -m-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Name"
                          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                        <Input
                          value={formData.role}
                          onChange={(e) => setFormData({...formData, role: e.target.value})}
                          placeholder="Role"
                          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Department</label>
                        <Input
                          value={formData.department}
                          onChange={(e) => setFormData({...formData, department: e.target.value})}
                          placeholder="Department"
                          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">LinkedIn URL</label>
                        <Input
                          value={formData.linkedin_url}
                          onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                          placeholder="LinkedIn URL"
                          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <ImageFieldWithPicker
                          label="Team Member Photo"
                          value={formData.image_url}
                          onChange={(url) => setFormData({...formData, image_url: url})}
                          onClear={() => setFormData({...formData, image_url: ''})}
                          previewShape="rounded-full"
                          uploadCategory="team"
                          uploadAssetKeyPrefix="team_member"
                          placeholder="Select team member photo"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Bio</label>
                        <Input
                          value={formData.bio}
                          onChange={(e) => setFormData({...formData, bio: e.target.value})}
                          placeholder="Bio"
                          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                          style={{ color: '#111827', backgroundColor: '#ffffff' }}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <PublishStatusCard
                        isPublished={formData.is_active}
                        onChange={(v) => setFormData({...formData, is_active: v})}
                        entityLabel="team member"
                        publicRoute="/about#team"
                        testId="team-publish-toggle"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={cancelEdit} className="bg-white text-gray-700 border-gray-300">
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={() => handleUpdate(member.id)} className="bg-green-600 hover:bg-green-700 text-white">
                        <Save className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-center gap-4">
                    {/* Photo */}
                    <img 
                      src={member.image_url} 
                      alt={member.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white/20 flex-shrink-0"
                    />
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{member.name}</h3>
                        {member.linkedin_url && (
                          <a href={member.linkedin_url} target="_blank" rel="noopener noreferrer">
                            <Linkedin className="w-4 h-4 text-blue-500" />
                          </a>
                        )}
                        {!member.is_active && (
                          <Badge variant="outline" className="text-xs text-red-500 border-red-500/50">Inactive</Badge>
                        )}
                      </div>
                      <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>{member.role}</p>
                      <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-400'}`}>{member.department}</p>
                      {member.bio && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>{member.bio}</p>
                      )}
                    </div>
                    
                    {/* Order */}
                    <div className="flex flex-col items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => handleReorder(member.id, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <span className={`text-xs font-mono ${isDark ? 'text-white/40' : 'text-gray-400'}`}>#{member.order}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => handleReorder(member.id, 'down')}
                        disabled={index === teamMembers.length - 1}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleToggleActive(member)}
                        className={member.is_active ? 'text-orange-500 border-orange-500/50' : 'text-green-500 border-green-500/50'}
                      >
                        {member.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => startEdit(member)}
                        className={isDark ? 'border-white/20' : ''}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDelete(member.id, member.name)}
                        className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
}
